import {isUndefined} from 'lodash';
import React, {Fragment} from 'react';
import styled from 'styled-components';

import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {TeamAvatar} from '../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {DropdownItemTeamAvatar} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemTeamAvatar';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {
  FilterRowGenerator,
  ResourceMultiSelectFilterDropdown,
} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceSync} from '../../../components/common/filters/sourceFilter/filterDropdownSource';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {getFlatTeams, getTeams} from '../../../store/data/teams/teamsSelectors';
import {makeFilterRows} from '../../helpers/analyticsFiltersHelpers';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.TEAMS];

/*
 * Style.
 */

const StyledTeamAvatar = styled(TeamAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

/*
 * Hook.
 */

export const useAnalyticsTeamsFilter = (): AnalyticsFilterItem => {
  const [selectedItemIds, onUpdate] = useAnalyticsFilter(AnalyticsFilterTypesEnum.TEAMS);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.TEAMS);

  const allTeams = useAppSelector(getTeams);
  const teams = useAppSelector(getFlatTeams);

  const onSelectItems = (updatedSelectedItems: ReadonlyArray<number>) => {
    onUpdate(updatedSelectedItems);
    trackOnEdit(updatedSelectedItems, selectedItemIds);
  };

  const isActive = !isUndefined(selectedItemIds) && selectedItemIds?.length > 0;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedItemIds}
      itemsMap={allTeams}
      iconName={isActive ? activeIconName : iconName}
      label={name}
      renderItem={renderButtonItem}
      isActive={isActive}
    />
  );

  const dropdownSource = computeFilterDropdownSourceSync(teams, filterRows);
  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ResourceMultiSelectFilterDropdown
      {...dropdownProps}
      dropdownTitle={name}
      selectedItemIds={selectedItemIds}
      onSetItems={onSelectItems}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={trackOnRemove}
    />
  );

  return {
    id: 'teams-filter',
    type: AnalyticsFilterTypesEnum.TEAMS,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const computeFilterGenerator: FilterRowGenerator<TeamModel> = (
  item,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isChecked = dropdownSelectedIds.includes(item.id);

  return {
    renderer: () => (
      <>
        <DropdownItemTeamAvatar team={item} />
        <DropdownItemContent isChecked={isChecked}>{item.name}</DropdownItemContent>
      </>
    ),
    onSelect: makeOnItemSelect(item.id),
    isSelected: isChecked,
  };
};

const filterRows = makeFilterRows<TeamModel>((item) => item.name);

const renderButtonItem = (team: TeamModel) => (
  <Fragment key={team.id}>
    <StyledTeamAvatar team={team} size={VisualSizesEnum.SMALL} /> {team.name}
  </Fragment>
);
